<template>
    <div class="custom-breadcrumb">
        <ul style="display: flex; align-items: center">
            <li>
                <div class="item" @click="chatMainMenu('/')">
                    {{ $t("message.home") }}
                </div>
            </li>
            <li>
                <div
                    class="item integration"
                    :class="{ 'last-item': isIntegrationLastItem }"
                    @click="go('/integrations')"
                    v-if="doesRouteHasIntegrations"
                >
                    {{ $t("message.integrations") }}
                </div>
            </li>
            <li v-if="taskRouteHas">
                <div
                    class="item tasks"
                    :class="{ 'last-item': isTasksLastItem }"
                    @click="workflowList"
                >
                    Tasks
                </div>
            </li>
            <template v-if="paths">
                <li
                    v-for="(item, index) in paths"
                    :key="'br-' + index"
                    class="d-flex align-items-center"
                >
                    <div
                    class="item sec"
                        :class="{ 'last-item': index === paths.length - 1 }"
                    :colorize="'c-' + index"
                    @click="item.path === 'chat' ? chatMainMenu(item.path) : go(item.path)"
                >
                    {{ item.title }}
                </div>
            </li>
            </template>
            <li v-if="showSubjects">
                <select id="departSelect" v-model="selectedSubjectModel">
                    <option value="null">{{ $t("message.allDepartments") }}</option>
                    <option v-for="subject in subjects" :key="subject._id" :value="subject._id">
                        {{ getSubjectName(subject) }}
                    </option>
                </select>
            </li>
            <li v-if="select" class="d-flex align-items-center">
                <slot></slot>
            </li>
            <li class="ml-auto">
                <div class="d-flex justify-content-between align-items-center">
                    <div
                        v-if="showTrialRemainDay && trialInfo.trial && settings && settings.plan"
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                            theme: isDarkMode ? 'light-border' : 'google',
                        }"
                        :content="$t('billing.trialTimeInformation', { dayCount: trialInfo.remainingDays })"
                        class="item four mr-2 ml-2"
                        style="
                            overflow: hidden;
                            text-overflow: ellipsis;
                            display: -webkit-box;
                            -webkit-box-orient: vertical;
                            -webkit-line-clamp: 1;
                            word-break: break-all;
                        "
                    >
                        <span class="text-info bold" @click="$router.push('/settings/billing')">
                            {{ $t("billing.trialTimeInformationShort", { dayCount: trialInfo.remainingDays }) }}
                        </span>
                    </div>
                    <button
                        v-if="customerDomainsFetched && !isSiteIntegrated()"
                        @click.prevent="integrationInstruction"
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                            theme: isDarkMode ? 'light-border' : 'google',
                        }"
                        :content="$t('message.notIntegratedDesc')"
                        class="actionButtonBreadcrumb"
                    >
                        <md-icon style="color: red">support</md-icon>
                        {{ $t("message.notIntegrated") }}
                    </button>
                </div>
            </li>
            <li
                v-if="activeBroadcast"
                class="active-broadcast-item"
                @click="goToActiveBroadcast"
                v-tippy="{
                    followCursor: true,
                    placement: 'bottom-end',
                    theme: isDarkMode ? 'light-border' : 'google',
                }"
                :content="getActiveBroadcastTooltip()"
            >
                <div class="active-broadcast-content">
                    <span class="material-icons broadcast-icon">podcasts</span>
                    <span class="broadcast-label">Toplu Mesaj:</span>
                    <span class="broadcast-title">{{ truncateBroadcastTitle(activeBroadcast.title) }}</span>
                    <span class="broadcast-progress">{{ getBroadcastPercent(activeBroadcast) }}%</span>
                </div>
            </li>
            <!-- 1) WiFi Icon -->
            <li v-if="largeHeader">
                <md-button
                    style="min-width: 20px; max-width: 25px"
                    v-tippy="{
                        followCursor: true,
                        placement: 'bottom-end',
                        theme: isDarkMode ? 'light-border' : 'google',
                    }"
                    :content="socketConnected ? $t('message.connectServer') : $t('message.disconnectServer')"
                    class="actionButtonBreadcrumb m-0 w-100 act-btn wifi-status-button"
                    @click="disconnectSocket"
                >
                    <div :class="{ blinkAnimation: !socketConnected }">
                        <WifiIcon
                            class="wifi-icon"
                            :color="socketConnected ? '#25CC52' : 'red'"
                        />
                    </div>
                </md-button>
            </li>
            <!-- 2) Chatbot Icon -->
            <li>
                <ChatbotStatusWidget />
            </li>
            <!-- 3) Storage Icon -->
            <li>
                <div
                    :leftPercentTile="getStorageStatus()"
                    class="storage-information-item"
                    @mouseenter="storageIconHovered = true"
                    @mouseleave="storageIconHovered = false"
                >
                    <StorageIcon
                        class="storage-icon"
                        :color="storageIconHovered ? (getStorageStatus() === 'high' ? 'rgb(111, 200, 98)' : getStorageStatus() === 'medium' ? 'rgb(0, 164, 255)' : 'rgb(168, 40, 40)') : '#3A3F48'"
                    />
                    <div v-if="storageIconHovered" class="custom-tooltip" v-html="storageTooltipContent"></div>
                </div>
            </li>
            <!-- 4) Voice Agent Icon -->
            <li v-if="voiceAgentChannelExists">
                <VoiceAgentChannelStatusWidget v-once />
            </li>
            <!-- 5) Telegram Icon -->
            <li
                v-if="largeHeader && telegramBotEnabled"
                @mouseenter="telegramIconHovered = true"
                @mouseleave="telegramIconHovered = false"
            >
                <md-button
                    style="min-width: 20px; max-width: 25px"
                    class="actionButtonBreadcrumb m-0 wp-btn telegram-status-button"
                    @click="$router.push('/integrations')"
                >
                    <TelegramIcon
                        class="telegram-icon"
                        :color="telegramIconHovered ? (!telegramBotInfo.initialized ? 'gray' : telegramServiceIsHealthy ? 'green' : '#e24444') : '#3A3F48'"
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                            theme: isDarkMode ? 'light-border' : 'google',
                        }"
                        :content="
                            !telegramBotInfo.initialized
                                ? $t('message.telegramBotIsReachable')
                                : telegramServiceIsHealthy
                                ? $t('message.telegramBotIsHealthy')
                                : $t('message.telegramBotIsUnhealthy')
                        "
                    />
                </md-button>
            </li>
            <!-- 6) WhatsApp Icon -->
            <li v-if="whatsappChannelExists">
                <ChannelsStatus />
            </li>
            <!-- 7) Instagram Icon -->
            <li v-if="igChannelExists">
                <IGChannelsStatus />
            </li>
            <!-- 8) Messenger Icon -->
            <li v-if="messengerChannelExists">
                <MessengerChannelStatusWidget />
            </li>
            <!-- 9) SMS Icon -->
            <li v-if="channelSmsVisable">
                <ChannelStatusPostaCell />
            </li>
            <!-- 10) Agent Icon -->
            <li
                v-if="largeHeader"
                class="agent-status-li"
                @mouseenter="agentIconHovered = true"
                @mouseleave="agentIconHovered = false"
            >
                <md-button
                    style="min-width: 20px; max-width: 25px"
                    v-tippy="{
                        followCursor: true,
                        placement: 'bottom-end',
                        theme: isDarkMode ? 'light-border' : 'google',
                    }"
                    :content="offlineMode ? $t('message.liveChatOff') : $t('message.liveChatOn')"
                    class="actionButtonBreadcrumb m-0 act-btn agent-status-button"
                >
                    <AgentIcon
                        class="agent-icon"
                        :color="agentIconHovered ? (offlineMode ? 'red' : 'green') : '#3A3F48'"
                    />
                </md-button>
            </li>
            <li
                class="wallet-balance-item"
                v-tippy="{
                    followCursor: true,
                    placement: 'bottom-end',
                    theme: isDarkMode ? 'light-border' : 'google',
                }"
                v-if="($route.path === '/chat' || $route.query.tab === 'inbox') && $_.get(wallet, 'whatsapp.amount')"
                @click="$router.push('/settings/wallet')"
                :content="'Cüzdan Bakiyeniz: ' + seperateWithCommas(wallet.whatsapp.amount.toFixed(2)) + '₺'"
            >
                <div class="wallet-balance-content" :class="{
                    'negative-balance': wallet.whatsapp.amount < 0,
                    'low-balance': wallet.whatsapp.amount >= 0 && wallet.whatsapp.amount < 200
                }">
                    <MoneyFormatter :price="wallet.whatsapp.amount" className="wallet-amount" />
                    <WalletIcon
                        class="wallet-icon"
                        :color="wallet.whatsapp.amount < 0 ? '#FF2525' : (wallet.whatsapp.amount < 200 ? '#FFA800' : '#25CC52')"
                    />
                </div>
            </li>
            <!-- Web Tab Button -->
            <li v-if="largeHeader">
                <WebTabButton />
            </li>
            <!--<li v-if="largeHeader" class="item four">
                <span class="date">{{ $t("time.today") }}:</span><span class="time">{{ getToday() }}</span>
            </li>-->
        </ul>
        <HeaderRight v-if="!largeHeader" />
    </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import Arrows from "../svg/Arrows";
import HeaderRight from "../HeaderRight";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";
import ChannelsStatus from "./ChannelStatusWidget.vue";
import IGChannelsStatus from "./IGChannelStatusWidget.vue";
import ChannelStatusPostaCell from "./ChannelStatusPostaCell.vue";
import Chatbot from "../svg/stepper/Chatbot.vue";
import MoneyFormatter from "../MoneyFormatter.vue";
import MessengerChannelStatusWidget from "./MessengerChannelStatusWidget.vue";
import VoiceAgentChannelStatusWidget from "./VoiceAgentChannelStatusWidget.vue";
import ChatbotStatusWidget from "./ChatbotStatusWidget.vue";
import WalletIcon from "../svg/appBreadCrumb/WalletIcon.vue";
import AgentIcon from "../svg/appBreadCrumb/AgentIcon.vue";
import SMSIcon from "../svg/appBreadCrumb/SMSIcon.vue";
import TelegramIcon from "../svg/appBreadCrumb/TelegramIcon.vue";
import WifiIcon from "../svg/appBreadCrumb/WifiIcon.vue";
import ChatbotIcon from "../svg/appBreadCrumb/ChatbotIcon.vue";
import StorageIcon from "../svg/appBreadCrumb/StorageIcon.vue";
import WebTabButton from "./WebTabButton.vue";
import WHATSAPP_API from "../../api/whatsapp";
import SYSTEM_PUBSUB from "../../utils/systemPubSub";


library.add(faInstagram);
export default {
    name: "AppBreadCrumb",
    components: {
        MessengerChannelStatusWidget,
        Arrows,
        HeaderRight,
        TelegramIcon,
        ChannelsStatus,
        IGChannelsStatus,
        ChannelStatusPostaCell,
        Chatbot,
        MoneyFormatter,
        VoiceAgentChannelStatusWidget,
        ChatbotStatusWidget,
        WalletIcon,
        AgentIcon,
        SMSIcon,
        WifiIcon,
        ChatbotIcon,
        StorageIcon,
        WebTabButton
    },
    data() {
        return {
            customerDomainsFetched: false,
            wpInterval: null,
            doesRouteHasIntegrations: false,
            taskRouteHas: false,
            channelsVisible: false,
            channelSmsVisable: false,
            agentIconHovered: false,
            telegramIconHovered: false,
            storageIconHovered: false,
            activeBroadcast: null,
            broadcastPollingInterval: null,
        };
    },
    props: {
        paths: {
            type: Array,
            required: false,
        },
        select: {
            type: Boolean,
            required: false,
        },
        showSubjects: {
            type: Boolean,
            required: false,
            default: false,
        },
        showTrialRemainDay: {
            type: Boolean,
            default: true,
        },
        showChatbotSession: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            channelsBusiness: (state) => state.channel.channels.filter((x) => x.type === "whatsapp-business-api" || x.type === "whatsapp-business-api-supsis"),
            channelsV2: (state) => state.channel.channels.filter((x) => x.type === "whatsapp-v2"),
            channelsV1: (state) =>
                state.channel.channels.filter((x) => x.type === "whatsapp-v1" || x.type === "whatsapp-qr"),
            whatsappChannelExists: (state) =>
                state.channel.channels.filter((x) =>
                    ["whatsapp-v1", "whatsapp-v2", "whatsapp-unipile", "whatsapp-business-api", "whatsapp-business-api-supsis"].includes(x.type)
                ).length > 0,
            igChannelExists: (state) => state.channel.channels.filter((x) => ["instagram", "instagram-v2"].includes(x.type)).length > 0,
            messengerChannelExists: (state) =>
                state.channel.channels.filter((x) => ["messenger"].includes(x.type)).length > 0,
            wpInfo: (state) => state.whatsapp.info,
            telegramBotInfo: (state) => state.telegrambot.info,
            largeHeader: (state) => state.app.largeHeader,
            settings: (state) => state.setting.settings,
            customerDomains: (state) => state.visitor.customerDomains,
            site: (state) => state.site.site,
            socketConnected: (state) => state.socketStatus.socket_connected,
            debugMode: (state) => state.app.debugMode,
            socket: (state) => state.socketStatus.current_socket,
            offlineMode: (state) => state.setting.offlineMode,
            activeConversation: (state) =>
                state.app.fakeMode ? state.chat.fakeActiveConversation : state.chat.activeConversation,
            selectedSubject: (state) => state.subject.selectedSubject,
            trialInfo: (state) => {
                const site = state.site.site;
                if (site) {
                    const { createdAt } = site;
                    const diff = Math.ceil(moment().diff(moment(createdAt), "days", true));
                    const tookDays = Math.max(diff, 0);
                    const isTrial = tookDays <= _.get(state.setting.settings, "plan.trialDays");
                    return {
                        trial: isTrial,
                        remainingDays: isTrial ? _.get(state.setting.settings, "plan.trialDays") - tookDays : 0,
                    };
                } else {
                    return { trial: false, remainingDays: 0 };
                }
            },
            wallet: (state) => state.wallet.wallet,
            channels: (state) => state.channel.channels.filter((x) => x.type === "sms-postacell"),
            storage: (state) => state.site.storage,
            voiceAgentChannelExists: (state) =>
                state.channel.channels.filter((x) => ["retell"].includes(x.type)).length > 0,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            subjects: "subject/getUserSubjects",
            telegramBotEnabled: "setting/telegramBotEnabled",
            telegramServiceIsHealthy: "telegrambot/isHealthy",
        }),
        selectedSubjectModel: {
            get() {
                return this.selectedSubject;
            },
            set(value) {
                this.updateSelectedSubject(value);
            },
        },
        storageTooltipContent() {
            return `
                <div class='left-align-tooltip'>
                   ${this.$t('message.storageSpace ')}: ${this.storage.maxDiskSpace / 1024 / 1024 / 1024} GB<br/>
                   ${this.$t('message.usedMemory')}: ${this.storage.percentage.toFixed(1)}%<br/>
                   ${this.$t('message.availableMemory')}: ${(100 - this.storage.percentage).toFixed(1)}%<br>
                   ${this.$t('message.memoryLimit')}: ${(this.storage.exceededSpace / 1024 / 1024 / 1024).toFixed(2)} GB<br>
                   ${this.$t('message.memoryLimitPrice')}: ${((this.storage.exceededSpace / 1024 / 1024 / 1024) * this.storage.perGBPrice).toFixed(2)}₺
                </div>
            `;
        },
        isIntegrationLastItem() {
            // Eğer paths yoksa veya boşsa ve taskRouteHas false ise, integration son item olur
            return (!this.paths || this.paths.length === 0) && !this.taskRouteHas;
        },
        isTasksLastItem() {
            // Eğer paths yoksa veya boşsa, tasks son item olur
            return !this.paths || this.paths.length === 0;
        },
    },
    methods: {
        ...mapActions({
            getCustomerDomainsIfNeed: "visitor/getCustomerDomainsIfNeed",
            updateSelectedSubject: "subject/updateSelectedSubject",
            toggleLargeHeader: "app/toggleLargeHeader",
        }),
        workflowList() {
            this.$emit("type-workflow-list");
        },
        seperateWithCommas(x) {
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },

        integrationInstruction() {
            this.$router.push("/integrations/webchat/website-integration");
        },
        toggle() {
            this.toggleLargeHeader();
        },
        go(path) {
            if (path) {
                this.$router.push(path);
            }
        },
        getToday() {
            moment.locale(this.$i18n.locale);
            return moment().format("Do MMM");
        },
        chatMainMenu(path) {
            if (path) {
                this.$router.push(path);
                this.$emit("to-chat-main", path);
            }
        },
        disconnectSocket() {
            if (this.debugMode && this.socketConnected) {
                this.socket.disconnect();
            }
        },
        isSiteIntegrated() {
            return this.customerDomainsFetched && this.customerDomains.length;
        },
        getSubjectName(subject) {
            const validLangs = Object.keys(subject.languageContent);
            const lang = validLangs.includes(this.$i18n.locale) ? this.$i18n.locale : validLangs[0];
            return _.get(subject, `languageContent.${lang}.name`);
        },
        channelSmsAvailability() {
            if (this.channels.length > 0) {
                this.channelSmsVisable = true;
            }
        },
        getStorageStatus() {
            const leftStorage = 100 - this.storage.percentage;
            return leftStorage > 70 ? "high" : leftStorage <= 70 && leftStorage > 30 ? "medium" : "low";
        },
        async fetchActiveBroadcasts() {
            try {
                const last24Hours = moment().subtract(24, 'hours').toISOString();
                const now = moment().toISOString();
                const broadcasts = await WHATSAPP_API.BROADCAST.getBroadcastList(50, '', last24Hours, now);
                const activeStatuses = ['scheduled', 'running', 'queued', 'created'];
                
                const activeBroadcasts = broadcasts.filter(b => activeStatuses.includes(b.status));
                
                if (activeBroadcasts.length > 0) {
                    const runningBroadcast = activeBroadcasts.find(b => b.status === 'running');
                    this.activeBroadcast = runningBroadcast || activeBroadcasts[0];
                } else {
                    this.activeBroadcast = null;
                }
            } catch (error) {
                console.error('Failed to fetch active broadcasts:', error);
            }
        },
        startBroadcastPolling() {
            this.fetchActiveBroadcasts();
            this.broadcastPollingInterval = setInterval(() => {
                this.fetchActiveBroadcasts();
            }, 60*1000);
        },
        stopBroadcastPolling() {
            if (this.broadcastPollingInterval) {
                clearInterval(this.broadcastPollingInterval);
                this.broadcastPollingInterval = null;
            }
        },
        truncateBroadcastTitle(title) {
            if (!title) return '';
            return title.length > 15 ? title.substring(0, 15) + '...' : title;
        },
        getBroadcastPercent(broadcast) {
            if (!broadcast || !broadcast.total) return 0;
            const sent = (broadcast.sent || 0) + (broadcast.failed || 0);
            return Math.round((sent / broadcast.total) * 100);
        },
        getActiveBroadcastTooltip() {
            if (!this.activeBroadcast) return '';
            const statusLabels = {
                created: 'Oluşturuldu',
                scheduled: 'Zamanlandı',
                queued: 'Kuyrukta',
                running: 'Gönderiliyor',
            };
            const sent = (this.activeBroadcast.sent || 0) + (this.activeBroadcast.failed || 0);
            return `${this.activeBroadcast.title}<br/>Durum: ${statusLabels[this.activeBroadcast.status]}<br/>İlerleme: ${sent}/${this.activeBroadcast.total}`;
        },
        goToActiveBroadcast() {
            if (!this.activeBroadcast) return;
            const broadcastData = {
                _id: this.activeBroadcast._id,
                title: this.activeBroadcast.title,
                status: this.activeBroadcast.status,
                total: this.activeBroadcast.total,
                sent: this.activeBroadcast.sent,
                failed: this.activeBroadcast.failed,
                createdAt: this.activeBroadcast.createdAt,
            };
            
            this.$router.push({
                path: '/chat',
                query: {
                    tab: 'inbox',
                    inbox: 'allContacts',
                    view: 'kanban',
                    field: 'lastBroadcastStatus',
                },
            });
            
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('select-broadcast-from-breadcrumb', {
                    detail: broadcastData
                }));
            }, 800);
        },
    },
    mounted() {
        this.channelSmsAvailability();
        this.getCustomerDomainsIfNeed().then((x) => {
            this.customerDomainsFetched = true;
        });
        const route = this.$route.path;
        const integrationPath = "/integrations/";
        if (route.includes(integrationPath)) {
            this.doesRouteHasIntegrations = true;
        }
        if (route.includes("/tasks") && !route.includes("/billing")) {
            this.taskRouteHas = true;
        }
        
        this.startBroadcastPolling();
        
        const broadcastCreatedSub = SYSTEM_PUBSUB.stream.subscribe(({ type, payload }) => {
            if (type === SYSTEM_PUBSUB.MESSAGE_TYPES.BROADCAST_CREATED) {
                this.fetchActiveBroadcasts();
            }
        });
        
        this.$once('hook:beforeDestroy', () => {
            broadcastCreatedSub.unsubscribe();
        });
    },
    beforeDestroy() {
        if (this.wpInterval) clearInterval(this.wpInterval);
        this.stopBroadcastPolling();
    },
};
</script>

<style lang="scss">
.left-align-tooltip {
    text-align: left;
}

.actionButtonBreadcrumb {
    .md-button-content {
        display: flex !important;
        align-items: center;
        justify-content: center;
    }

    // Sadece ikon butonları için stil
    &.agent-status-button,
    &.telegram-status-button,
    &.wifi-status-button {
        background: none !important;
        box-shadow: none !important;

        &:hover {
            background: none !important;
        }

        &:before {
            display: none !important;
        }
    }

    // Tüm ikonları eşit boyutlandır
    .agent-icon,
    .telegram-icon,
    .wifi-icon,
    .chatbot-icon,
    .storage-icon {
        width: 18px !important;
        height: 18px !important;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            width: 18px !important;
            height: 18px !important;
        }
    }
}

.ant-menu-submenu-arrow {
    position: unset !important;
}

.custom-breadcrumb {
    position: sticky;
    top: 0;
    z-index: 998;

    .storage-information-item {
        background-color: transparent;
        color: #ffa800 !important;
        font-family: Poppins;
        font-size: 12px;
        cursor: pointer;
        padding: 4px 6px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        .storage-icon {
            width: 18px !important;
            height: 18px !important;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .material-icons {
            font-size: 18px;
        }

        svg {
            width: 18px !important;
            height: 18px !important;
        }

        .custom-tooltip {
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            margin-top: 8px;
            background-color: #333;
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            white-space: nowrap;
            z-index: 9999;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            pointer-events: none;

            &::after {
                content: '';
                position: absolute;
                bottom: 100%;
                left: 50%;
                transform: translateX(-50%);
                border: 6px solid transparent;
                border-bottom-color: #333;
            }
        }

        &[leftPercentTile="low"] {
            background-color: transparent;

            .material-icons {
                color: rgb(168, 40, 40) !important;
            }
        }

        &[leftPercentTile="medium"] {
            background-color: transparent;

            .material-icons {
                color: rgb(0 164 255) !important;
            }
        }

        &[leftPercentTile="high"] {
            background-color: transparent;

            .material-icons {
                color: rgb(111, 200, 98) !important;
            }
        }
    }
}
</style>
<style lang="scss" scoped>
#departSelect {
    border: none;
    background-color: #f3f6f9;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-family: Poppins;
    font-weight: bolder;
    color: #3699ff;
}

.new-theme {
    .blinkAnimation {
        i,
        svg {
            animation: blinkAnim 1s infinite;
        }
    }

    @keyframes blinkAnim {
        from {
            color: #f90000;
            opacity: 0;
        }
        to {
            color: #ff0a0a;
            opacity: 1;
        }
    }

    .custom-breadcrumb {
        padding: 15px;
        background-color: #ffffff;
        height: 45px;
        display: flex;
        align-items: center;
        width: 100%;
        border-bottom: 1px solid var(--color-border-default);

        ul {
            list-style: none;
            display: flex;
            margin: 0;
            padding: 0;
            width: 100%;
            gap: 4px;
            align-items: center;

            li {
                display: flex;
                align-items: center;

                &:last-child {
                    border-right: none;
                }

                // Sadece status ikon butonları için boyutlandırma (slot içindeki butonları etkilemez)
                > .md-button.agent-status-button,
                > .md-button.telegram-status-button,
                > .md-button.wifi-status-button,
                > .storage-information-item {
                    min-width: 32px !important;
                    max-width: 32px !important;
                    min-height: 32px !important;
                    max-height: 32px !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }
        }

        path {
            fill: red;
        }

        .item {
            font-family: Poppins;
            font-size: 11px;
            font-weight: 500;
            color: #7C8DB5;
            cursor: pointer;
            padding: 8px 2px;
            transition: color 0.3s ease;
            display: inline-flex;
            align-items: center;
            background-color: transparent;
            border-radius: 0;

            &:hover {
                color: #1A1D1F;
            }

            &::after {
                content: '›';
                margin-left: 4px;
                margin-right: 4px;
                font-size: 16px;
                color: #7C8DB5;
                font-weight: 400;
            }

            &.sec {
                font-weight: 500;
                color: #7C8DB5;

                &:hover {
                    color: #1A1D1F;
                }

                &[colorize="c-0"],
                &[colorize="c-1"],
                &[colorize="c-2"] {
                    background-color: transparent;
                    color: #7C8DB5;
                    border-radius: 0;
                    padding: 8px 2px;
                }

                &.last-item {
                    color: #1A1D1F;
                    font-weight: 600;
                    margin-bottom: -1px;

                    &::after {
                        display: none;
                    }
                }
            }

            &.integration {
                font-weight: 500;
                color: #7C8DB5;
                background-color: transparent;
                border-radius: 0;
                padding: 8px 2px;
                font-size: 11px;

                &:hover {
                    color: #1A1D1F;
                }

                &.last-item {
                    color: #1A1D1F;
                    font-weight: 600;

                    &::after {
                        display: none;
                    }
                }
            }

            &.tasks {
                font-weight: 500;
                color: #7C8DB5;
                background-color: transparent;
                border-radius: 0;
                padding: 8px 2px;
                font-size: 11px;

                &:hover {
                    color: #1A1D1F;
                }

                &.last-item {
                    color: #1A1D1F;
                    font-weight: 600;

                    &::after {
                        display: none;
                    }
                }
            }

            &.third {
                font-weight: 500;
                color: #7C8DB5;
                background-color: transparent;
                border-radius: 0;
                padding: 8px 2px;
                font-size: 11px;

                &:hover {
                    color: #1A1D1F;
                }
            }

            &.four {
                background-color: #f3f6f9;
                border-radius: 6px;
                padding: 3px 10px;
                font-size: 12px;

                &::after {
                    display: none;
                }

                span.date {
                    color: #b5b5c3;
                    font-family: Poppins;
                    font-weight: 500;
                    font-size: 13px;
                }

                span.time {
                    color: #3699ff !important;
                    font-family: Poppins;
                    font-weight: 600;
                    font-size: 13px;
                    padding-left: 10px;
                }
            }
        }

    }

    .active-broadcast-item {
        border-right: none !important;
        margin-right: 0 !important;
        padding-right: 0 !important;
        cursor: pointer;
        
        .active-broadcast-content {
            background: #E8F5FE;
            padding: 4px 10px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            gap: 5px;
            transition: background 0.2s ease;
            
            &:hover {
                background: #D3E9FD;
            }
            
            .broadcast-icon {
                font-size: 16px;
                color: #1E88E5;
                animation: pulse 2s ease-in-out infinite;
            }
            
            .broadcast-label {
                color: #1565C0;
                font-family: Poppins, sans-serif;
                font-weight: 500;
                font-size: 11px;
            }
            
            .broadcast-title {
                color: #1565C0;
                font-family: Poppins, sans-serif;
                font-weight: 600;
                font-size: 11px;
                max-width: 100px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            
            .broadcast-progress {
                color: #1976D2;
                font-family: Poppins, sans-serif;
                font-weight: 600;
                font-size: 11px;
                background: rgba(255, 255, 255, 0.6);
                padding: 2px 5px;
                border-radius: 4px;
            }
        }
    }
    
    @keyframes pulse {
        0%, 100% {
            opacity: 1;
        }
        50% {
            opacity: 0.6;
        }
    }

    .wallet-balance-item {
        border-right: none !important;
        margin-right: 0 !important;
        padding-right: 0 !important;

        .wallet-balance-content {
            background: #E7FFF1;
            padding: 4px 12px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            gap: 6px;
            cursor: pointer;

            .wallet-amount {
                color: #25CC52 !important;
                font-family: Poppins, sans-serif;
                font-weight: 600 !important;
                font-size: 13px !important;
            }

            .wallet-icon {
                width: 18px;
                height: 18px;
                display: flex;
                align-items: center;
                justify-content: center;

                svg {
                    width: 100%;
                    height: 100%;
                }
            }

            &.low-balance {
                background: #FFF9E6;

                .wallet-amount {
                    color: #FFA800 !important;
                }
            }

            &.negative-balance {
                background: #FFE7E7;

                .wallet-amount {
                    color: #FF2525 !important;
                }
            }
        }
    }
}

#arrows {
    cursor: pointer;
    transition: all 0.3s;
    transform: rotateZ(90deg);

    &.open {
        transform: rotateZ(270deg);
    }
}
</style>
